import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useRevit } from '../../contexts/RevitContext';
import { Key, Copy, Check, Trash2, Plus, ChevronDown } from 'lucide-react';
import './ApiKeys.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const BASE_URL = 'https://api.getrevtrack.com/api';

const MCP_URL = 'https://api.getrevtrack.com/mcp';

const ENDPOINTS = [
    {
        method: 'GET',
        path: '/public/v1/data',
        description: 'Per-resource clicks, sales, signups, call bookings, revenue, and funnel steps. Events recorded without a resource are not included.',
        responseExample: {
            success: true,
            count: 1,
            data: [
                {
                    id: 42,
                    category: 'video',
                    name: 'videoId',
                    youtube_video_id: 'videoId',
                    youtube_title: 'How I Made $10k in 30 Days',
                    views: 15420,
                    engaged_views: 4200,
                    published_at: '2024-03-01T00:00:00.000Z',
                    link_url: 'https://example.com/offer',
                    link_tags: ['tag1'],
                    first_event: '2024-03-02T00:00:00.000Z',
                    clicks: 312,
                    sales: 18,
                    callBookings: 5,
                    signups: 92,
                    revenue: 2970.0,
                    offers: [
                        {
                            offer_id: 7,
                            offer_name: 'Course Launch',
                            conversion_value: 197,
                            offer_type: 'paid',
                            clicks: 312,
                            sales: 18,
                            callBookings: 0,
                            signups: 0,
                            revenue: 2970.0,
                            funnel_steps: [
                                { step_name: 'Application Submitted', funnel_step_id: 3, conversion_count: 4, conversion_value: 0 },
                            ],
                        },
                    ],
                },
            ],
        },
    },
    {
        method: 'GET',
        path: '/public/v1/data-by-date',
        description: 'Clicks, sales, signups, call bookings, and funnel steps in a date range, including events with no resource. Each event includes referrer and device.',
        params: '?startDate=2024-01-01&endDate=2024-12-31',
        responseExample: {
            success: true,
            data: {
                clicks: [
                    {
                        timestamp: '2024-06-15T10:22:00.000Z',
                        type: 'video',
                        resource_name: 'videoId',
                        youtube_title: 'How I Made $10k',
                        offer_name: 'Course Launch',
                        offer_type: 'paid',
                        referrer: 'https://youtube.com/watch?v=videoId',
                        device: 'mobile',
                    },
                ],
                sales: [
                    {
                        timestamp: '2024-06-15T11:00:00.000Z',
                        type: 'video',
                        resource_name: 'videoId',
                        offer_name: 'Course Launch',
                        offer_type: 'paid',
                        value: 197,
                        referrer: 'https://youtube.com/watch?v=videoId',
                        device: 'mobile',
                    },
                ],
                signups: [
                    {
                        timestamp: '2024-06-15T12:00:00.000Z',
                        type: null,
                        resource_name: null,
                        offer_name: 'Newsletter',
                        offer_type: 'signup',
                        referrer: 'https://instagram.com/',
                        device: 'desktop',
                    },
                ],
                call_bookings: [],
                funnel_steps: {
                    'Application Submitted': [
                        {
                            timestamp: '2024-06-15T11:05:00.000Z',
                            resource_name: 'videoId',
                            offer_name: 'High Ticket',
                            referrer: 'https://youtube.com/watch?v=videoId',
                            device: 'mobile',
                        },
                    ],
                },
                currency: 'USD',
            },
        },
    },
    {
        method: 'GET',
        path: '/public/v1/data-by-offer/:offerId',
        description: 'All the stats for one offer. totals counts every conversion, including those with no resource. Get the offer ID from /offers.',
        responseExample: {
            success: true,
            data: {
                offer: { id: 7, name: 'Course Launch' },
                totals: { clicks: 316, sales: 19, callBookings: 5, signups: 0, revenue: 3167.0 },
                unattributed: { clicks: 4, sales: 1, callBookings: 0, signups: 0, revenue: 197.0, funnel_steps: [] },
                resources: [
                    {
                        resource_id: 42,
                        category: 'video',
                        name: 'videoId',
                        youtube_title: 'How I Made $10k',
                        clicks: 312,
                        sales: 18,
                        callBookings: 0,
                        signups: 0,
                        revenue: 2970.0,
                        funnel_steps: [],
                    },
                ],
            },
        },
    },
    {
        method: 'GET',
        path: '/public/v1/data-by-resource/:resourceName',
        description: 'All offer stats for a specific resource. For Youtube videos, resourceName is the video ID.',
        responseExample: {
            success: true,
            data: {
                resource: {
                    id: 42,
                    type: 'video',
                    name: 'videoId',
                    youtube_title: 'How I Made $10k',
                    views: 15420,
                    published_at: '2024-03-01T00:00:00.000Z',
                },
                offers: [
                    {
                        offer_id: 7,
                        offer_name: 'Course Launch',
                        conversion_value: 197,
                        clicks: 312,
                        sales: 18,
                        callBookings: 0,
                        signups: 0,
                        revenue: 2970.0,
                        funnel_steps: [],
                    },
                ],
            },
        },
    },
    {
        method: 'GET',
        path: '/public/v1/offers',
        description: 'List all your offers and their funnel steps',
        responseExample: {
            success: true,
            count: 1,
            data: [
                {
                    id: 7,
                    name: 'Course Launch',
                    conversion_values: [197, 497],
                    offer_type: 'paid',
                    is_custom: true,
                    call_booking_required: false,
                    steps: [
                        { step_name: 'Application Submitted', step_order: 1, conversion_value: 0 },
                        { step_name: 'Call Completed', step_order: 2, conversion_value: 497 },
                    ],
                },
            ],
        },
    },
    {
        method: 'GET',
        path: '/public/v1/conversions',
        description: 'Latest conversions and each lead journey, including conversions with no resource. Includes referrer, device, country, and lead details. Optional startDate and endDate (YYYY-MM-DD, inclusive) filter by event time. Omit both to return every conversion.',
        params: '?startDate=2024-08-01&endDate=2024-08-31',
        responseExample: {
            currency: 'USD',
            sales: [
                {
                    sale_id: 1,
                    lead_id: 'abc-123',
                    value: 197,
                    offer_name: 'Course Launch',
                    offer_type: 'paid',
                    resource_type: 'video',
                    resource_name: 'videoId',
                    initial_referrer: 'https://youtube.com/watch?v=videoId',
                    device: 'mobile',
                    timestamp: '2024-06-15T11:00:00.000Z',
                    is_test: false,
                    conversion_type: 'sale',
                    emails: ['lead@example.com'],
                    name: 'Alex',
                    country_code: 'US',
                    related_events: [
                        {
                            event_type: 'click',
                            timestamp: '2024-06-14T09:00:00.000Z',
                            resource_name: 'videoId',
                            offer_name: 'Course Launch',
                            initial_referrer: 'https://youtube.com/watch?v=videoId',
                            device: 'mobile',
                        },
                    ],
                },
            ],
            other_conversions: [
                {
                    sale_id: 2,
                    lead_id: 'def-456',
                    value: 0,
                    offer_name: 'Newsletter',
                    offer_type: 'signup',
                    resource_type: null,
                    resource_name: null,
                    initial_referrer: 'https://instagram.com/',
                    device: 'desktop',
                    timestamp: '2024-06-16T09:00:00.000Z',
                    is_test: false,
                    conversion_type: 'email_signup',
                    emails: [],
                    name: null,
                    country_code: null,
                    related_events: [],
                },
            ],
        },
    },
    {
        method: 'GET',
        path: '/public/v1/audience-geo',
        description: 'Country counts for clicks, calls, sales, or email signups in a date range. eventType is clicks, calls, sales, or emails (default clicks). offerId is optional.',
        params: '?startDate=2024-08-01&endDate=2024-08-31&eventType=emails',
        responseExample: {
            success: true,
            data: {
                eventType: 'emails',
                startDate: '2024-08-01T00:00:00.000Z',
                endDate: '2024-08-31T23:59:59.999Z',
                offerId: null,
                countries: [
                    { code: 'US', iso3: 'USA', name: 'United States', count: 42 },
                    { code: 'GB', iso3: 'GBR', name: 'United Kingdom', count: 11 },
                ],
                totalEvents: 60,
                locatedEvents: 53,
                unknownEvents: 7,
            },
        },
    },
    {
        method: 'GET',
        path: '/public/v1/clicks',
        description: 'Tracked clicks, including clicks with no resource. Each click includes referrer and device.',
        responseExample: {
            currency: 'USD',
            clicks: [
                {
                    click_id: 55,
                    lead_id: 'abc-123',
                    offer_name: 'Course Launch',
                    resource_type: 'video',
                    resource_name: 'videoId',
                    youtube_title: 'How I Made $10k',
                    initial_referrer: 'https://youtube.com/watch?v=videoId',
                    device: 'mobile',
                    is_test: false,
                    timestamp: '2024-06-14T09:00:00.000Z',
                },
            ],
        },
    },
];

const LANGS = [
    { id: 'js',    label: 'JavaScript' },
    { id: 'curl',  label: 'cURL' },
    { id: 'python',label: 'Python' },
    { id: 'node',  label: 'Node.js' },
];

function buildSnippet(ep, lang) {
    const url = `${BASE_URL}${ep.path}${ep.params || ''}`;
    const isPost = ep.method === 'POST';
    const bodyJson = ep.body ? JSON.stringify(ep.body, null, 2) : null;

    if (lang === 'js') {
        const lines = [
            `const response = await fetch('${url}', {`,
            ...(isPost ? [`  method: 'POST',`] : []),
            `  headers: {`,
            `    'Authorization': 'Bearer YOUR_API_KEY',`,
            ...(isPost ? [`    'Content-Type': 'application/json',`] : []),
            `  },`,
            ...(isPost ? [`  body: JSON.stringify(${bodyJson.replace(/\n/g, '\n  ')}),`] : []),
            `});`,
            ``,
            `const data = await response.json();`,
        ];
        return lines.join('\n');
    }

    if (lang === 'node') {
        const lines = [
            `const axios = require('axios');`,
            ``,
            `const { data } = await axios.${isPost ? 'post' : 'get'}(`,
            `  '${url}',`,
            ...(isPost ? [`  ${bodyJson.replace(/\n/g, '\n  ')},`] : []),
            `  {`,
            `    headers: {`,
            `      Authorization: 'Bearer YOUR_API_KEY',`,
            ...(isPost ? [`      'Content-Type': 'application/json',`] : []),
            `    },`,
            `  }`,
            `);`,
        ];
        return lines.join('\n');
    }

    if (lang === 'python') {
        const lines = [
            `import requests`,
            ``,
            `headers = {`,
            `    "Authorization": "Bearer YOUR_API_KEY",`,
            ...(isPost ? [`    "Content-Type": "application/json",`] : []),
            `}`,
            ``,
            ...(isPost
                ? [
                    `payload = ${bodyJson.replace(/\n/g, '\n')}`,
                    ``,
                    `response = requests.post("${url}", json=payload, headers=headers)`,
                  ]
                : [`response = requests.get("${url}", headers=headers)`]),
            `data = response.json()`,
        ];
        return lines.join('\n');
    }

    if (lang === 'curl') {
        const lines = [
            `curl -X ${ep.method} \\`,
            `  "${url}" \\`,
            `  -H "Authorization: Bearer YOUR_API_KEY"` + (isPost ? ` \\` : ''),
            ...(isPost
                ? [
                    `  -H "Content-Type: application/json" \\`,
                    `  -d '${JSON.stringify(ep.body)}'`,
                  ]
                : []),
        ];
        return lines.join('\n');
    }

    return '';
}

const ApiKeys = () => {
    const { username } = useRevit();
    const { getToken } = useAuth();
    const queryClient = useQueryClient();

    const [newlyCreatedKey, setNewlyCreatedKey] = useState(null);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [keyName, setKeyName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [copied, setCopied] = useState(false);
    const [keyToRevoke, setKeyToRevoke] = useState(null);
    const [showRevokeConfirmation, setShowRevokeConfirmation] = useState(false);
    const [isRevoking, setIsRevoking] = useState(false);
    const [selectedEndpoint, setSelectedEndpoint] = useState(null);
    const [snippetLang, setSnippetLang] = useState('js');
    const [copiedSnippet, setCopiedSnippet] = useState(false);
    const [copiedMcpUrl, setCopiedMcpUrl] = useState(false);

    const { data, isLoading, isError, error } = useQuery({
        queryKey: ['apiKeys', username],
        queryFn: async () => {
            const token = await getToken();
            const res = await fetch(`https://${SERVER_URL}/api/list-api-keys/${username}`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                throw new Error(body.error || `Request failed: ${res.status}`);
            }
            const json = await res.json();
            console.log('[ApiKeys] list response:', json.keys.fields);
            return json;
        },
        enabled: !!username,
    });

    const handleCreateKey = async (e) => {
        e.preventDefault();
        setIsCreating(true);
        try {
            const token = await getToken();
            const res = await fetch(`https://${SERVER_URL}/api/create-api-key`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({ username, key_name: keyName.trim() || null }),
            });
            if (!res.ok) throw new Error('Failed to create API key');
            const result = await res.json();
            setNewlyCreatedKey(result.apiKey);
            setShowCreateForm(false);
            setKeyName('');
            queryClient.invalidateQueries({ queryKey: ['apiKeys', username] });
        } catch (err) {
            console.error('Error creating API key:', err);
            alert('Failed to create API key. Please try again.');
        } finally {
            setIsCreating(false);
        }
    };

    const handleCopyKey = async () => {
        try {
            await navigator.clipboard.writeText(newlyCreatedKey);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            /* fallback: do nothing */
        }
    };

    const handleCopyMcpUrl = async () => {
        try {
            await navigator.clipboard.writeText(MCP_URL);
            setCopiedMcpUrl(true);
            setTimeout(() => setCopiedMcpUrl(false), 2000);
        } catch {
            /* fallback: do nothing */
        }
    };

    const handleRevokeClick = (key) => {
        setKeyToRevoke(key);
        setShowRevokeConfirmation(true);
    };

    const cancelRevoke = () => {
        setShowRevokeConfirmation(false);
        setKeyToRevoke(null);
    };

    const confirmRevoke = async () => {
        if (!keyToRevoke) return;
        setIsRevoking(true);
        try {
            const token = await getToken();
            const res = await fetch(`https://${SERVER_URL}/api/revoke-api-key/${keyToRevoke.id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` },
            });
            if (!res.ok) throw new Error('Failed to revoke API key');
            queryClient.invalidateQueries({ queryKey: ['apiKeys', username] });
            setShowRevokeConfirmation(false);
            setKeyToRevoke(null);
        } catch (err) {
            console.error('Error revoking API key:', err);
            alert('Failed to revoke API key. Please try again.');
        } finally {
            setIsRevoking(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Never';
        return new Date(dateString).toLocaleDateString(undefined, {
            year: 'numeric', month: 'short', day: 'numeric',
        });
    };

    return (
        <div className="api-keys-container">

            {/* ── Header ── */}
            <div className="api-keys-header">
                <div>
                    <p className="api-keys-title body-large-semibold">Revtrack API & MCP Server</p>
                    <p className="api-keys-subtitle body-small-regular">
                        Access RevTrack data from external tools and integrations.
                    </p>
                </div>
            </div>

            {/* ── Inline create form ── */}
            {showCreateForm && (
                <form className="create-key-form" onSubmit={handleCreateKey}>
                    <input
                        className="create-key-name-input"
                        type="text"
                        placeholder="Key name"
                        value={keyName}
                        onChange={(e) => setKeyName(e.target.value)}
                        autoFocus
                        maxLength={64}
                    />
                    <div className="create-key-form__actions">
                        <button
                            type="button"
                            className="cancel-button"
                            onClick={() => { setShowCreateForm(false); setKeyName(''); }}
                            disabled={isCreating}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="create-key-button"
                            disabled={isCreating || !keyName.trim()}
                        >
                            {isCreating ? 'Creating…' : 'Generate Key'}
                        </button>
                    </div>
                </form>
            )}

            {/* ── New key banner (shown once) ── */}
            {newlyCreatedKey && (
                <div className="new-key-banner">
                    <div className="new-key-banner__header">
                        <span className="new-key-banner__title">
                            ⚠&nbsp; Save your API key — it won't be shown again
                        </span>
                    </div>
                    <div className="new-key-banner__key-row">
                        <code className="new-key-banner__key">{newlyCreatedKey}</code>
                        <button className="copy-key-button" onClick={handleCopyKey}>
                            {copied ? <Check size={15} strokeWidth={2.5} /> : <Copy size={15} />}
                            {copied ? 'Copied!' : 'Copy'}
                        </button>
                    </div>
                    <button className="dismiss-key-button" onClick={() => setNewlyCreatedKey(null)}>
                        I've saved my key
                    </button>
                </div>
            )}

            {/* ── Keys table ── */}
            <div className="api-keys-card">
                <div className="api-keys-card__header">
                    <h2 className="api-keys-card__title body-medium-semibold">Your API Keys</h2>
                    {!showCreateForm && (
                        <button
                            className="create-key-button"
                            onClick={() => setShowCreateForm(true)}
                        >
                            <Plus size={16} strokeWidth={2.5} />
                            Create New Key
                        </button>
                    )}
                </div>

                {isLoading ? (
                    <div className="api-keys-empty">
                        <p>Loading keys…</p>
                    </div>
                ) : isError ? (
                    <div className="api-keys-empty" style={{ color: 'var(--Delete-Red)' }}>
                        <p>Failed to load keys: {error?.message}</p>
                    </div>
                ) : !data?.keys?.length ? (
                    <div className="api-keys-empty">
                        <Key size={32} color="var(--Text-Body3)" strokeWidth={1.5} />
                        <p>No API keys yet. Create one to get started.</p>
                    </div>
                ) : (
                    <table className="api-keys-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Created</th>
                                <th>Last Used</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.keys.map((key) => (
                                <tr key={key.id}>
                                    <td>
                                        {key.key_name
                                            ? <span className="key-name-label">{key.key_name}</span>
                                            : <span className="key-id-badge">#{key.id}</span>
                                        }
                                    </td>
                                    <td>{formatDate(key.created_at)}</td>
                                    <td className="last-used-cell">{formatDate(key.last_used_at)}</td>
                                    <td>
                                        <button
                                            className="revoke-button"
                                            onClick={() => handleRevokeClick(key)}
                                        >
                                            <Trash2 size={13} strokeWidth={2} />
                                            Revoke
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="api-keys-card connect-ai-card">
                <h2 className="api-keys-card__title body-medium-semibold">Connect to Revtrack's MCP Server</h2>
                <div className="connect-ai-steps">
                    <div className="connect-ai-step">
                        <span className="connect-ai-step__badge">Claude</span>
                        <div>
                            <p className="body-small-regular">In the Claude app, open Customize &rarr; Connectors &rarr; Add custom connector.</p>
                            <p className="connect-ai-step__detail body-small-regular">Paste the server URL into the connector URL field on that screen, then sign in with your RevTrack account.</p>
                        </div>
                    </div>
                    <div className="connect-ai-step">
                        <span className="connect-ai-step__badge">ChatGPT</span>
                        <div>
                            <p className="body-small-regular">
                                In ChatGPT, open{' '}
                                <a className="connect-ai-link" href="https://chatgpt.com/plugins" target="_blank" rel="noreferrer">chatgpt.com/plugins</a>
                                {' '}&rarr; Add &rarr; Add custom MCP server.
                            </p>
                            <p className="connect-ai-step__detail body-small-regular">Paste the server URL into the URL field there, select OAuth, and sign in with your RevTrack account when asked.</p>
                        </div>
                    </div>
                </div>
                <p className="connect-ai-url-label body-small-regular">
                    Server URL — copy this, then paste it into the URL field inside Claude or ChatGPT. Do not paste it into your browser&apos;s address bar.
                </p>
                <div className="connect-ai-url-row">
                    <code className="connect-ai-url">{MCP_URL}</code>
                    <button className="connect-ai-copy-button" onClick={handleCopyMcpUrl}>
                        {copiedMcpUrl ? <Check size={14} strokeWidth={2.5} /> : <Copy size={14} />}
                        {copiedMcpUrl ? 'Copied!' : 'Copy'}
                    </button>
                </div>
                <p className="connect-ai-description body-small-regular">
                    Once the connector is added, ask Claude or ChatGPT about your Revtrack data.
                </p>
            </div>

            {/* ── API Endpoints accordion ── */}
            <div className="api-keys-card">
                <h2 className="api-keys-card__title body-medium-semibold">Available API Endpoints</h2>

                <div className="endpoints-list">
                    <p className="endpoints-description body-small-regular">
                        Pass your key in the{' '}
                        <code className="inline-code-text">Authorization</code> header:&nbsp;
                        <code className="inline-code-text">Authorization: Bearer YOUR_API_KEY</code>
                    </p>
                    {ENDPOINTS.map((ep, i) => {
                        const isOpen = selectedEndpoint === i;
                        return (
                            <div key={i} className={`endpoint-item${isOpen ? ' endpoint-item--open' : ''}`}>
                                <button
                                    className="endpoint-row"
                                    onClick={() => {
                                        setSelectedEndpoint(isOpen ? null : i);
                                        setCopiedSnippet(false);
                                    }}
                                >
                                    <span className={`method-badge method-badge--${ep.method.toLowerCase()}`}>
                                        {ep.method}
                                    </span>
                                    <code className="endpoint-path">{ep.path}</code>
                                    <span className="endpoint-desc body-small-regular">{ep.description}</span>
                                    <ChevronDown
                                        size={14}
                                        color="var(--Text-Body3)"
                                        className={`endpoint-chevron${isOpen ? ' endpoint-chevron--open' : ''}`}
                                    />
                                </button>
                                {isOpen && (
                                    <>
                                        <div className="endpoint-snippet">
                                            <div className="endpoint-snippet__toolbar">
                                                <div className="snippet-lang-tabs">
                                                    {LANGS.map((l) => (
                                                        <button
                                                            key={l.id}
                                                            className={`snippet-lang-tab${snippetLang === l.id ? ' snippet-lang-tab--active' : ''}`}
                                                            onClick={() => { setSnippetLang(l.id); setCopiedSnippet(false); }}
                                                        >
                                                            {l.label}
                                                        </button>
                                                    ))}
                                                </div>
                                                <button
                                                    className="copy-key-button"
                                                    onClick={async () => {
                                                        await navigator.clipboard.writeText(buildSnippet(ep, snippetLang)).catch(() => {});
                                                        setCopiedSnippet(true);
                                                        setTimeout(() => setCopiedSnippet(false), 2000);
                                                    }}
                                                >
                                                    {copiedSnippet ? <Check size={13} strokeWidth={2.5} /> : <Copy size={13} />}
                                                    {copiedSnippet ? 'Copied!' : 'Copy'}
                                                </button>
                                            </div>
                                            <pre className="endpoint-snippet__code">{buildSnippet(ep, snippetLang)}</pre>
                                        </div>
                                        {ep.responseExample && (
                                            <div className="endpoint-response">
                                                <div className="endpoint-response__header">
                                                    <span className="endpoint-response__label">Response Format</span>
                                                </div>
                                                <pre className="endpoint-snippet__code endpoint-response__code">{JSON.stringify(ep.responseExample, null, 2)}</pre>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ── Revoke confirmation ── */}
            {showRevokeConfirmation && (
                <div className="confirmation-overlay">
                    <div className="confirmation-popup confirmation-popup--danger">
                        <div className="confirmation-popup__header">
                            <div className="confirmation-popup__icon">&#9888;</div>
                            <h3 className="confirmation-popup__title">Revoke API Key</h3>
                        </div>
                        <p className="confirmation-popup__body">
                            Are you sure you want to revoke{' '}
                            <strong>{keyToRevoke?.key_name || `#${keyToRevoke?.id}`}</strong>?
                            This action cannot be undone.
                        </p>
                        <div className="confirmation-popup__warning">
                            Any applications using this key will immediately lose access.
                        </div>
                        <div className="confirmation-popup__actions">
                            <button className="cancel-button" onClick={cancelRevoke} disabled={isRevoking}>
                                Cancel
                            </button>
                            <button className="proceed-button" onClick={confirmRevoke} disabled={isRevoking}>
                                {isRevoking ? 'Revoking…' : 'Yes, Revoke'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ApiKeys;
